/**
 * GERİ BİLDİRİM (kullanıcı kararı 25.10.2026): içe aktarılamayan satırlar ve
 * ekstre biçimi, kullanıcı gönderilecek metni birebir görüp "Gönder" derse
 * Parafon sunucusuna gider; bir sonraki geliştirmede incelenir.
 *
 * Metin MASKELİDİR (`ekstreMaske.ts` kuralları): rakam → 9, sözlükte olmayan
 * harf → X/x; kullanıcı kelime açıp kapatabilir. Metne Parafon'un kendi
 * iskeleti (başlık, bölüm ve sütun anahtarları) açık yazılır; bunlarda rakam
 * yoktur. Cihaz ve sunucu aynı denetimi (`geriBildirimDenetle`) uygular.
 */

import {
  GERI_BILDIRIM_KODU_UZUNLUGU,
  geriBildirimDenetle,
  type GeriBildirimKaynagi,
} from "@parafon/contracts/central-api";
import { M } from "../dil";
import { kelimeleriSay, maskele, type KelimeSayimi, type MaskeSecimi } from "./ekstreMaske";
import type { Sorun } from "./iceAktarma";

/** Bir gönderimde en çok bu kadar satır (gerisi aynı türden olur). */
export const GERI_BILDIRIM_SATIR_SINIRI = 30;

/** Gönderilecek satır: kaynak satır ve okuyucunun sebebi. */
export interface GeriBildirimSatiri {
  readonly kaynak: Sorun["kaynak"];
  /** Kaynak yoksa (CSV): dosyanın başlığı ve satırın hücreleri. */
  readonly baslik: readonly string[];
  readonly ham: readonly string[];
  readonly sebep: string;
}

/** Maskelenecek (kullanıcının belgesinden gelen) bütün metin parçaları. */
function veriParcalari(satirlar: readonly GeriBildirimSatiri[]): string[] {
  const out: string[] = [];
  for (const s of satirlar.slice(0, GERI_BILDIRIM_SATIR_SINIRI)) {
    if (s.kaynak !== undefined) for (const [, v] of s.kaynak.hucreler) out.push(v);
    else out.push(...s.baslik, ...s.ham);
    out.push(s.sebep);
  }
  return out;
}

/** Kelime seçimi için: satırlardaki kelimeler, en sık geçen önce. */
export function satirKelimeleri(satirlar: readonly GeriBildirimSatiri[]): readonly KelimeSayimi[] {
  const parcalar = veriParcalari(satirlar).map((metin) => ({ x: 0, y: 0, genislik: 0, boy: 0, metin }));
  return kelimeleriSay([{ no: 1, genislik: 0, yukseklik: 0, parcalar }]);
}

/** Gönderilecek metin; ekranda birebir bu gösterilir. */
export function satirMetni(bicimAdi: string, satirlar: readonly GeriBildirimSatiri[], secim: MaskeSecimi): string {
  const m = (x: string): string => maskele(x, secim);
  const out = [
    M.iceAktar.geriBildirim.metinBaslik,
    M.iceAktar.geriBildirim.metinBicim(bicimAdi),
    M.iceAktar.geriBildirim.metinKural,
    "",
  ];
  for (const s of satirlar.slice(0, GERI_BILDIRIM_SATIR_SINIRI)) {
    if (s.kaynak !== undefined) {
      const hucreler = s.kaynak.hucreler.map(([k, v]) => `${k}=${m(v)}`);
      out.push(`- ${s.kaynak.bolum} · ${hucreler.join(" · ")}`);
    } else {
      const hucreler = s.ham.map((v, i) => `${m(s.baslik[i] ?? "?")}=${m(v)}`).filter((h) => !h.endsWith("="));
      out.push(`- ${hucreler.join(" · ")}`);
    }
    out.push(`  ${M.iceAktar.geriBildirim.metinSorun} ${m(s.sebep)}`);
  }
  return `${out.join("\n")}\n`;
}

/** Sunucuya giden istek; gerçek taşıyıcı beyaz listeyi ve yasaklı alanları denetler. */
export type GeriBildirimIstegi = (yol: string, govde: Readonly<Record<string, string>>) => Promise<unknown>;

export function geriBildirimKoduGoster(kod: string): string {
  return kod.length === GERI_BILDIRIM_KODU_UZUNLUGU ? `${kod.slice(0, 4)}-${kod.slice(4)}` : kod;
}

function hataKodu(e: unknown): string | null {
  const c = (e as { code?: unknown } | null)?.code;
  return typeof c === "string" ? c : null;
}

/** Gönderir, başvuru kodunu döner. Hata anlaşılır cümleyle fırlar. */
export async function geriBildirimGonder(
  istek: GeriBildirimIstegi,
  kaynak: GeriBildirimKaynagi,
  metin: string,
): Promise<string> {
  const sebep = geriBildirimDenetle(kaynak, metin);
  if (sebep !== null) throw new Error(M.iceAktar.geriBildirim.gonderilemez(sebep));
  let yanit: unknown;
  try {
    yanit = await istek("/geri-bildirim", { kaynak, metin });
  } catch (e: unknown) {
    switch (hataKodu(e)) {
      case "RATE_LIMITED":
        throw new Error(M.iceAktar.geriBildirim.cokGonderim);
      case "DOLU":
        throw new Error(M.iceAktar.geriBildirim.dolu);
      case "BUYUK":
        throw new Error(M.iceAktar.geriBildirim.sinirAsiyor);
      default:
        if ((e as { status?: unknown } | null)?.status === 413) {
          throw new Error(M.iceAktar.geriBildirim.sinirAsiyor);
        }
        throw e;
    }
  }
  const kod = (yanit as { kod?: unknown } | null)?.kod;
  if (typeof kod !== "string") throw new Error(M.iceAktar.geriBildirim.yanitAnlasilamadi);
  return kod;
}
